// file created at 2020-2-4
// Auto-generated files ObservableMigration.ts

import { Vue, Component, Watch } from 'vue-property-decorator';

import * as d3 from 'd3';
import * as math from 'mathjs';
import Datum from '@/interfaces/Datum';
import AreaGroupRenderer from './AreaGroupRenderer';
import { getImageUrl, getImageJson } from '@/firebase/firebaseStorage';
import { Dictionary } from 'vue-router/types/router';
import * as _ from 'lodash';
import RightClickMenu from '@/components/RightClickMenu/RightClickMenu.vue';
import RightClickMenuTs from '@/components/RightClickMenu';
import { eventBus } from '@/utils/EventBus';
import ImagePaletteWidget from '@/components/ImagePaletteWidget/ImagePaletteWidget.vue';
import ImagePaletteWidgetTs from '@/components/ImagePaletteWidget';
import PaletteHistory from '@/components/PaletteHistory/PaletteHistory.vue';
import PaletteHistoryTs from '@/components/PaletteHistory';
import PaletteCopier from '@/components/PaletteCopier/PaletteCopier.vue';
import PaletteCopierTs from '@/components/PaletteCopier';
import InteractionRecord from '@/components/InteractionRecord/InteractionRecord.vue';
import InteractionRecordTs from '@/components/InteractionRecord';
import VisualHint from '@/components/VisualHint/VisualHint.vue';
import VisualHintTs from '@/components/VisualHint';
import ComponentController from '@/components/ComponentController/ComponentController.vue';
import ComponentControllerTs from '@/components/ComponentController';
import LayoutController from '@/components/LayoutController/LayoutController.vue';
import LayoutControllerTs from '@/components/LayoutController';
import { sortForDisplay } from '@/utils/palette-utils';
import { InteractionLabelsDict } from '@/interfaces/Etc';
import LinkGroup from './LinkGroup.vue';
import LinkGroupTs from './LinkGroup';

import * as hcm from './HCM';

@Component({
  components: {
    RightClickMenu,
    ImagePaletteWidget,
    PaletteHistory,
    PaletteCopier,
    InteractionRecord,
    VisualHint,
    ComponentController,
    LayoutController,
    LinkGroup
  }
})
export default class ObservableMigration extends Vue {
  public $refs!: {
    rightClickMenu: RightClickMenuTs;
    imagePaletteWidget: ImagePaletteWidgetTs;
    paletteHistory: PaletteHistoryTs;
    paletteCopier: PaletteCopierTs;
    interactionRecord: InteractionRecordTs;
    visualHint: VisualHintTs;
    layoutController: LayoutControllerTs;
    componentController: ComponentControllerTs;
    linkGroup: LinkGroupTs;
  };

  public showCutoffArea: boolean = false;
  public showComponentArea: boolean = false;

  public readonly components: hcm.Component[] = [];
  private componentSet: hcm.ComponentSet = new hcm.ComponentSet(this.components);

  private isForEnglishPaper: boolean = true; // English or 한글. user-test를 위한 레이아웃이 약간 변경될 수 있다.
  private showConcentricCurveGroup: boolean = false;

  private src: string = '';
  private rawData: Datum | undefined;

  private generationPolicy: 'number' | 'cutoff' = 'number';

  private forceNumberSliderDisabled = false;
  private forceCutoffSliderDisabled = false;

  private numComponents: number = 3;
  private readonly minNumComponents: number = 3;
  private readonly maxNumComponents: number = 8;

  private wardCutoff: number = 100;
  private minWardCutoff = 2;
  private maxWardCutoff: number = 1000; // it will be updated later

  private isDepthChanged: boolean = false; // to prevent redundant re-drawing
  private isNumberChanged: boolean = false; // to prevent redundant re-drawing
  private isCutoffChanged: boolean = false; // to prevent redundant re-drawing

  private svg!: d3.Selection<SVGSVGElement, any, HTMLElement, unknown>;
  private nodeGroup!: d3.Selection<SVGGElement, any, HTMLElement, unknown>;
  private concentricCurveGroup!: d3.Selection<SVGGElement, any, HTMLElement, unknown>;
  private verticalLayout: 'topdown' | 'bottomup' = 'topdown';
  private horizontalLayout: 'linearTidy' | 'radialTidy' = 'radialTidy';

  private collapseAfter = 2;

  private clickedNode: hcm.Component | undefined = undefined;

  private areaGroupRenderer: AreaGroupRenderer = new AreaGroupRenderer(this);

  private controllerShowed: boolean = true;
  private root!: hcm.Component;

  private mouseoverNode: d3.HierarchyPointNode<Datum> | null = null;

  private readonly interactionLabelsDict: InteractionLabelsDict = {
    english: {
      merge: 'Merge',
      split: 'Split',
      alter: 'Alter',
      revert: 'Revert'
    },
    korean: {
      merge: '병합',
      split: '분할',
      alter: '교체',
      revert: '복귀'
    }
  };

  public isClickedNode(node: hcm.Component) {
    return this.clickedNode === node;
  }

  @Watch('verticalLayout')
  @Watch('horizontalLayout')
  protected async onLayoutChanged() {
    this.updateLayout();
    await this.draw();
    await this.updateViewBox();
  }

  protected async onCollapseAfterChanged() {
    // this.componentSet.limitDepth(this.collapseAfter);
    this.componentSet.newLimitDepth(this.collapseAfter);
    await this.onLayoutChanged();
  }

  protected async loadComponents(components: hcm.Component[]) {
    if (this.componentSet.join(...components)) {
      this.componentSet.sort(hcm.compareWard);
      if (this.clickedNode && !this.componentSet.has(this.clickedNode)) {
        this.clickedNode = undefined;
      }
      await this.onCollapseAfterChanged();
    }
  }

  protected async mounted() {

    const { dirname, filename } = this.$route.query as Dictionary<string>;

    console.assert(!!dirname && !!filename);

    this.src = await getImageUrl(dirname + '/thumb', filename);
    this.rawData = await getImageJson<Datum>(dirname, filename.split('.')[0] + '.json');

    this.svg = d3
      .select<SVGSVGElement, any>('svg#chart')
      .style('background-color', 'none')
      .style('width', '100%')
      .style('height', '100%')
      .style('font', '10px sans-serif')
      .style('margin', '5px')
      .style('user-select', 'none')
      .on('click', async () => {
        if (this.clickedNode) {
          const parent = this.clickedNode.parent!;
          this.clickedNode = undefined;
          // this.$refs.interactionRecord.interactionRecords.pop();
          await this.drawHierarchy(250, parent);
        }
      });

    this.nodeGroup = d3
      .select<SVGGElement, any>('g#nodeGroup')
      .attr('stroke-linejoin', 'round')
      .attr('cursor', 'pointer')
      .attr('pointer-events', 'all');

    this.concentricCurveGroup = d3
      .select<SVGGElement, any>('g#concentricCurveGroup')
      .attr('fill', 'none')
      .attr('stroke', '#555')
      .attr('stroke-width', 1.0)
      .attr('stroke-dasharray', '5 5');

    const layout = this.getLayout();
    this.root = layout(d3.hierarchy(this.rawData)) as hcm.Component;
    this.root.eachAfter(node => {
      this.saveCurrentPosition(node);
      node.data.rgb = d3.lab.apply(null, node.data.lab).rgb();

      if (node.data.children && !node.data.layer) {
        if (node.data.children[0].layer && node.data.children[1].layer) {
          const leftLayer = node.data.children[0].layer;
          const rightLayer = node.data.children[1].layer;
          node.data.layer = [0, 1].map(i => [...leftLayer[i], ...rightLayer[i]]) as [number[], number[]];
        }
      }

    });

    const wards = this.root.descendants().map(d => d.data.ward).sort((a, b) => b - a);
    this.minWardCutoff = 0.5 * (wards[this.maxNumComponents - 1] + wards[this.maxNumComponents - 2]);
    this.maxWardCutoff = 0.5 * (wards[this.minNumComponents - 1] + wards[this.minNumComponents - 2]);

    const newComponents = hcm.findComponents(this.root, hcm.compareWard, nodes => nodes.length < this.numComponents);
    this.componentSet.add(...newComponents);
    this.wardCutoff = hcm.getMaximumWard(this.components);

    window.addEventListener('resize', _.debounce(this.onLayoutChanged, 250));

    window.addEventListener('contextmenu', e => {
      if (this.$refs.rightClickMenu) { this.$refs.rightClickMenu.close(); }
      e.preventDefault();
    }, false);
    window.addEventListener('click', e => {
      if (this.$refs.rightClickMenu) { this.$refs.rightClickMenu.close(); }
    }, false);

    eventBus.$on('componentNodeDeleted', async (deletingComponentNode: hcm.Component) => {
      this.componentSet.remove(deletingComponentNode);
      await this.drawHierarchy();
    });
    // this.$refs.interactionRecord.initialComponentNodes = sortForDisplay([...this.componentNodes]); // for records

    /**
     * Initial Drawing
     */
    this.componentSet.limitDepth(this.collapseAfter);
    this.updateLayout();
    await this.draw(0, this.root);
    await this.updateViewBox();
  }

  private updateLayout() {
    this.getLayout()(this.root);
  }

  private updateViewBox(duration: number = 250) {
    return this.svg.transition().duration(duration).attr('viewBox', this.autoBox).end();
  }


  private draw(duration: number = 500, source?: hcm.Component | undefined) {

    if (this.horizontalLayout === 'linearTidy') {
      // before the change of layout
      this.showConcentricCurveGroup = false;
    }

    return Promise.all([
      /**
       * Draw Hierarchy
       */
      this.drawHierarchy(duration, source),
      /**
       * Draw Hinting Area
       */
      this.drawHintingArea(),
      /**
       * Draw Concentric Circles
       */
      drawConcentricCircles(this.root, this.concentricCurveGroup)
    ]).then(() => {

      if (this.horizontalLayout === 'radialTidy') {
        // after the change of layout
        this.showConcentricCurveGroup = true;
      }

    });
  }

  private drawHierarchy(duration: number = 500, source?: hcm.Component | undefined) {
    const nodes = this.root.descendants().reverse();
    const links = this.root.links();
    const horizontalLayout = this.horizontalLayout;
    return Promise.all([
      this.drawNodes(this.nodeGroup, nodes, duration, source),
      this.$refs.linkGroup.drawLinks(links, horizontalLayout, duration, source)
    ]).then(() => {
      this.root.each(this.saveCurrentPosition);
    });
  }

  private drawNodes(
    nodeGroup: d3.Selection<SVGGElement, any, HTMLElement, unknown>,
    nodes: Array<d3.HierarchyPointNode<Datum>>,
    duration: number = 500,
    source?: hcm.Component | undefined
  ): Promise<void> {

    const node = nodeGroup.selectAll<SVGGElement, hcm.Component>('g')
      .data(nodes, d => `${d.data.id}`);

    const nodeEnter = node.enter()
      .append('g')
      .attr('transform', d => {
        const src = source ? source : d.data.source;
        const o = { x: src.data.x0!, y: src.data.y0! };
        return this.getNodeTransform(o);
      })
      .attr('stroke-width', 4)
      .attr('fill-opacity', 0)
      .attr('stroke-opacity', 0)
      .on('click', async d => {
        d3.event.stopPropagation();
        if (this.componentSet.has(d)) {
          await this.onSelectedComponentClicked(d);
        } else {
          await this.onRejectedComponentClicked(d);
        }
      })
      .on('mouseover', d => {
        this.mouseoverNode = d;
      })
      .on('mouseout', d => {
        this.mouseoverNode = null;
      });
    /**
     * right-click-menu is implemented.
     */
    // .on('contextmenu', d => {
    //   // 오른쪽 클릭 메뉴를 화면에 띄워줘야 한다.
    //   if (this.componentSet.has(d)) {
    //     this.$refs.rightClickMenu.open({
    //       x: d3.event.x,
    //       y: d3.event.y,
    //       clickedNode: d,
    //       canUnselect: true,
    //       canSplit: true,
    //       canFold: false,
    //       canUnfold: false,
    //     });
    //   } else {
    //     if (this.isCollapsible(d)) {
    //       if (hcm.isFolded(d)) {
    //         this.$refs.rightClickMenu.open({
    //           x: d3.event.x,
    //           y: d3.event.y,
    //           clickedNode: d,
    //           canUnselect: false,
    //           canSplit: false,
    //           canFold: false,
    //           canUnfold: true,
    //         });
    //       } else {
    //         this.$refs.rightClickMenu.open({
    //           x: d3.event.x,
    //           y: d3.event.y,
    //           clickedNode: d,
    //           canUnselect: false,
    //           canSplit: false,
    //           canFold: true,
    //           canUnfold: false,
    //         });
    //       }
    //     }
    //   }

    //   d3.event.preventDefault();
    //   d3.event.stopPropagation();
    // });

    nodeEnter
      .append('circle')
      .call(this.drawNodeCircle);

    nodeEnter
      .append('text')
      .call(this.drawNodeText);

    // Transition nodes to their new position.
    const nodeUpdate = node.merge(nodeEnter);

    const nodeUpdateTransition = nodeUpdate
      .transition().duration(duration)
      .attr('transform', this.getNodeTransform)
      .attr('fill-opacity', 1)
      .attr('stroke-opacity', 1);

    nodeUpdateTransition
      .selectAll<SVGCircleElement, hcm.Component>('circle')
      .call(this.drawNodeCircle);

    nodeUpdate
      .selectAll<SVGTextElement, hcm.Component>('text')
      .call(this.drawNodeText);

    // Transition exiting nodes to the parent's new position.
    node.exit().transition().duration(duration)
      .attr('transform', (d: any) => {
        const src = source ? source : d.data.source;
        const o = { x: src.x!, y: src.y! };
        return this.getNodeTransform(o);
      })
      .attr('fill-opacity', 0)
      .attr('stroke-opacity', 0)
      .remove();

    return nodeUpdateTransition.end();
  }



  private saveCurrentPosition(node: hcm.Component) {
    node.data.x0 = node.x;
    node.data.y0 = node.y;
  }

  private drawNodeCircle(circle) {
    return circle
      .attr('fill', d => d.data.rgb)
      .attr('stroke', this.getNodeStroke)
      .attr('r', this.getNodeRadius);
  }

  private drawNodeText(text) {
    text
      .attr('dy', '0.40em')
      .attr('x', d => {
        if (this.isForEdit(d)) {
          return 0;
        } else {
          return d.x < Math.PI === !d.children ? 10 : -10;
        }
      })
      .attr('text-anchor', d => {
        if (this.isForEdit(d)) {
          return 'middle';
        } else {
          return d.x < Math.PI === !d.children ? 'start' : 'end';
        }
      })
      .attr('transform', this.getNodeTextTransform)
      .attr('font-size', this.getNodeTextFontSize)
      .text(this.getNodeText);
  }

  private setClickedNode(node: hcm.Component) {
    //     {
    //       this.$refs.interactionRecord.interactionRecords.push({
    //         sourceNode: node,
    //         targetNodes: [],
    //         interactionLabel: null
    //       });
    //     }
    this.clickedNode = node;
    return this.drawHierarchy(125, node.parent!);
  }

  private applyAlter(node: hcm.Component) {
    // {
    //   this.$refs.interactionRecord.saveAlter({
    //     node,
    //     interactionLabelsDict: this.interactionLabelsDict,
    //     isForEnglishPaper: this.isForEnglishPaper
    //   });
    // }
    this.saveComponents();
    this.componentSet.remove(node.parent!);
    this.componentSet.add(node);
    this.componentSet.sort(hcm.compareWard);

    this.clickedNode = undefined;

    return this.onCollapseAfterChanged();
  }

  private applySplit(node: hcm.Component) {
    //     {
    //       this.$refs.interactionRecord.saveSplit({
    //         node,
    //         interactionLabelsDict: this.interactionLabelsDict,
    //         isForEnglishPaper: this.isForEnglishPaper
    //       });
    //     }
    this.saveComponents();
    this.componentSet.remove(node);
    this.componentSet.add(...node.children!);
    this.componentSet.sort(hcm.compareWard);

    this.clickedNode = undefined;

    return this.onCollapseAfterChanged();
  }

  private applyMerge(node: hcm.Component) {
    //     {
    //       this.$refs.interactionRecord.saveMerge({
    //         node,
    //         interactionLabelsDict: this.interactionLabelsDict,
    //         isForEnglishPaper: this.isForEnglishPaper
    //       });
    //     }
    this.saveComponents();
    this.componentSet.remove(...node.parent!.children!);
    this.componentSet.add(node.parent!);
    this.componentSet.sort(hcm.compareWard);

    this.clickedNode = undefined;

    return this.onCollapseAfterChanged();
  }

  private applyRevert(node: hcm.Component) {
    //     {
    //       this.$refs.interactionRecord.saveRevert({
    //         node,
    //         interactionLabelsDict: this.interactionLabelsDict,
    //         isForEnglishPaper: this.isForEnglishPaper
    //       });
    //     }
    this.saveComponents();
    this.componentSet.remove(this.clickedNode!);
    this.componentSet.add(node);
    this.componentSet.sort(hcm.compareWard);

    this.clickedNode = undefined;

    return this.onCollapseAfterChanged();
  }

  /**
   * 컴포넌트인 노드가 클릭됐을 때 불리는 함수
   */
  private onSelectedComponentClicked(newClickedNode: hcm.Component) {
    if (this.clickedNode) {
      if (this.isForSplit(newClickedNode)) {
        // [Split] - the selected component is clicked again.
        return this.applySplit(newClickedNode);
      }
      if (this.isForMerge(newClickedNode)) {
        // [Merge] - the sibling of the selected component is clicked.
        return this.applyMerge(newClickedNode);
      }
    } else if (d3.event.ctrlKey) {
      this.componentSet.remove(newClickedNode);
      return this.onCollapseAfterChanged();
    } else {
      return this.setClickedNode(newClickedNode);
    }
  }

  /**
   * 컴포넌트가 아닌 노드가 클릭됐을 때 불리는 함수
   */
  private onRejectedComponentClicked(newClickedNode: hcm.Component) {
    if (this.clickedNode) {
      if (this.isForAlter(newClickedNode)) {
        // [Alter] - a child of the selected component is clicked.
        return this.applyAlter(newClickedNode);
      }
      if (this.isForRevert(newClickedNode)) {
        // [Revert] - the parent of the selected component is clicked.
        return this.applyRevert(newClickedNode);
      }
    } else {

      const newComponentNodes = [newClickedNode];

      const selectedDescendants = newClickedNode.descendants()
        .filter(descendant => this.componentSet.has(descendant));

      const selectedAncestor = newClickedNode.ancestors()
        .filter(ancestor => this.componentSet.has(ancestor)).slice(-1)[0];

      if (selectedDescendants.length) {
        this.saveComponents();
        this.componentSet.remove(...selectedDescendants);
      } else if (selectedAncestor) {

        if (d3.event.ctrlKey) {
          // Ctrl 키를 누른 경우, 클릭된 노드에 도달할 때까지 Split을 수행하는 효과를 낸다.
          let ancestors = newClickedNode.ancestors();
          ancestors = ancestors.slice(0, ancestors.indexOf(selectedAncestor));
          const ancestorSiblings = ancestors.map(hcm.getSibling);
          newComponentNodes.push(...ancestorSiblings);

          const numComponents = newComponentNodes.length + this.components.length;
          if (numComponents - 1 > this.maxNumComponents) {
            console.warn(`The number of components cannot exceed the maximum: ${numComponents - 1} > ${this.maxNumComponents}`);
            return undefined;
          }
        }/*else {
          // 그렇지 않은 경우, 선택된 노드에 도달할 때까지 Alter를 수행하는 효과를 낸다.
        }*/

        this.saveComponents();
        this.componentSet.remove(selectedAncestor);
      }

      this.componentSet.add(...newComponentNodes);
      this.componentSet.sort(hcm.compareWard);
      return this.onCollapseAfterChanged();
    }
  }

  private isForEdit(node): boolean {
    const fs = [
      this.isForAlter,
      this.isForSplit,
      this.isForMerge,
      this.isForRevert
    ];
    return fs.some(f => f(node));
  }

  private isForAlter(node: hcm.Component) {
    return node.parent && this.isClickedNode(node.parent);
  }

  private isForSplit(node: hcm.Component) {
    if (this.components.length >= 8) { return false; }
    return this.componentSet.has(node) && this.isClickedNode(node) && node.children;
  }

  private isForMerge(node: hcm.Component) {
    return this.componentSet.has(node)
      && node.parent
      && node.parent.children!.some(d => d !== node && this.isClickedNode(d));
  }

  private isForRevert(node: hcm.Component) {
    return node.children
      && node.children.some(c => this.isClickedNode(c)) // one of the children is clicked
      && !node.children.every(c => hcm.hasComponent(this.components, c)); // not all of the children are component
  }

  private isCollapsible(node: hcm.Component) {
    const foldable = node.children && !node.children.some(c => hcm.hasComponent(this.components, c));
    const unfoldable = !node.children && node.data._children;
    return foldable || unfoldable;
  }

  private drawHintingArea() {
    const linkGroupSelection = d3.select(this.$refs.linkGroup.$el as SVGGElement);
    const links = linkGroupSelection.selectAll<SVGPathElement, d3.HierarchyPointLink<Datum>>('path');
    return this.areaGroupRenderer.render(links, this.horizontalLayout);
  }

  private getSvgElement(): SVGSVGElement {
    const svgElement = this.svg.node();
    if (svgElement) {
      return svgElement;
    }
    throw new Error('svgElement should be defined');
  }

  private getParentElement(): HTMLElement {
    const parentElement = this.getSvgElement().parentElement;
    if (parentElement) {
      return parentElement;
    }
    throw new Error('parentElement should be defined');
  }

  private getRect(pl = 128, pt = 128, pr = 128, pb = 128): DOMRect {
    const rect: DOMRect = this.getParentElement().getBoundingClientRect() as DOMRect;
    rect.x += pl;
    rect.y += pt;
    rect.width -= pl + pr;
    rect.height -= pt + pb;
    return rect;
  }

  private getLayout() {
    const { width, height } = this.getRect();
    const extent = math.min(width, height);
    const radius = extent / 2;
    const radialSize: [number, number] = [2 * Math.PI, radius];

    if (this.verticalLayout === 'topdown') {
      if (this.horizontalLayout === 'radialTidy') {
        return d3
          .tree<Datum>()
          .size(radialSize)
          .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);
      } else if (this.horizontalLayout === 'linearTidy') {
        return (root: d3.HierarchyNode<Datum>) => {
          root.data.dx = 10;
          root.data.dy = extent / (root.height + 1);
          // return d3.tree<Datum>().nodeSize([root.data.dx, root.data.dy])(root);
          return d3.tree<Datum>().size([width, height])(root);
        };
      }
    } else if (this.verticalLayout === 'bottomup') {
      if (this.horizontalLayout === 'radialTidy') {
        return d3.cluster<Datum>().size(radialSize);
      } else if (this.horizontalLayout === 'linearTidy') {
        return (root: d3.HierarchyNode<Datum>) => {
          root.data.dx = 10;
          root.data.dy = extent / (root.height + 1);
          // root.data.dy = 40;
          // return d3.cluster<Datum>().nodeSize([root.data.dx, root.data.dy])(root);
          return d3.cluster<Datum>().size([width, height])(root);
        };
      }
    }

    throw new Error('Unsupported verticalLayout');
  }

  private autoBox() {
    const rect = this.getRect();
    let { x, y } = rect;
    const { width, height } = rect;
    if (this.horizontalLayout === 'radialTidy') {
      x = -width / 2;
      y = -height / 2;
    } else {
      x = 0;
      y = 0;
    }
    return `${[x, y, width, height]}`;
  }

  private getNodeStroke(node: hcm.Component) {
    if (this.isClickedNode(node)) {
      return 'green';
    }
    if (this.isForEdit(node)) {
      return 'red';
    }
    if (this.componentSet.has(node)) {
      return 'black';
    }
    return 'none';
  }

  private getNodeRadius(node: hcm.Component) {
    const maxDepth = this.root.descendants().slice(-1)[0].depth;
    const baseSize = Math.max(5, 8 - maxDepth + 4);
    if (this.isForEdit(node)) {
      return 2.5 * baseSize;
    }
    if (this.componentSet.has(node)) {
      return 2.0 * baseSize;
    }
    return baseSize;
  }

  private getNodeTransform(d: { x: number, y: number }) {
    if (this.horizontalLayout === 'radialTidy') {
      return `
        rotate(${(d.x * 180) / Math.PI - 90})
        translate(${d.y},0)
      `;
    } else {
      return `translate(${d.x},${d.y})`;
    }
  }

  private getNodeTextTransform(d: hcm.Component): string {
    const nodeRadius = this.getNodeRadius(d);
    let transformAttribute: string = '';
    if (this.horizontalLayout === 'radialTidy') {
      if (this.isForEdit(d)) {
        transformAttribute = `rotate(${-(d.x * 180 / Math.PI - 90)})`;
      } else {
        transformAttribute = d.x >= Math.PI ? 'rotate(180)' : '';
      }
      transformAttribute += this.isForEdit(d) ?
        ` translate(0, ${nodeRadius + 8})` : '';
    } else { // linearTidy
      transformAttribute = 'translate(18, 14)';
    }

    return transformAttribute;
  }

  private getNodeTextFontSize(d: hcm.Component): number {
    const maxDepth = this.root.descendants().slice(-1)[0].depth;
    const baseSize = Math.max(13, 8 - maxDepth + 4);
    return baseSize;
  }

  private getNodeText(node: hcm.Component) {
    let nodeText: string = '';
    if (!node.children) {
      if (node.data.numLeaves > 1) { nodeText = `+${node.data.numLeaves}`; }
    }
    if (this.isForAlter(node)) {
      nodeText = this.isForEnglishPaper ?
        this.interactionLabelsDict.english.alter : this.interactionLabelsDict.korean.alter;
    }
    if (this.isForSplit(node)) {
      nodeText = this.isForEnglishPaper ?
        this.interactionLabelsDict.english.split : this.interactionLabelsDict.korean.split;
    }
    if (this.isForMerge(node)) {
      nodeText = this.isForEnglishPaper ?
        this.interactionLabelsDict.english.merge : this.interactionLabelsDict.korean.merge;
    }
    if (this.isForRevert(node)) {
      nodeText = this.isForEnglishPaper ?
        this.interactionLabelsDict.english.revert : this.interactionLabelsDict.korean.revert;
    }
    return nodeText;
  }

  private saveComponents() {
    this.$refs.paletteHistory.save();
  }

  private showCutoffAreaChanged(showCutoffArea: boolean) {
    this.showCutoffArea = showCutoffArea;
  }
  private showComponentAreaChanged(showComponentArea: boolean) {
    this.showComponentArea = showComponentArea;
  }

  private async handleNumberSliderEvent() {
    this.saveComponents();
    const conditionFn = nodes => nodes.length < this.numComponents;
    this.componentSet.join(...hcm.findComponents(this.root, hcm.compareWard, conditionFn));
    this.wardCutoff = hcm.getMaximumWeight(this.components, hcm.getWard);
    await this.onCollapseAfterChanged();
    eventBus.$emit('handleNumberSliderEventEnd');
  }
  private async handleCutoffSliderEvent() {
    this.saveComponents();
    const conditionFn = nodes => hcm.getMaximumWeight(nodes, hcm.getWard) > this.wardCutoff;
    this.componentSet.join(...hcm.findComponents(this.root, hcm.compareWard, conditionFn));
    this.numComponents = this.components.length;
    await this.onCollapseAfterChanged();
    eventBus.$emit('handleCutoffSliderEventEnd');
  }
  private async numberOfInitialComponentsRadioChanged() {
    const conditionFn = nodes => nodes.length < this.numComponents;
    this.componentSet.join(...hcm.findComponents(this.root, hcm.compareWard, conditionFn));
    this.wardCutoff = hcm.getMaximumWeight(this.components, hcm.getWard);
    await this.onCollapseAfterChanged();
  }

  private numComponentsChanged(numComponents: number) {
    this.numComponents = numComponents;
  }
  private wardCutoffChanged(wardCutoff: number) {
    this.wardCutoff = wardCutoff;
  }

  private verticalLayoutChanged(verticalLayout: 'topdown' | 'bottomup') {
    this.verticalLayout = verticalLayout;
  }
  private horizontalLayoutChanged(horizontalLayout: 'linearTidy' | 'radialTidy') {
    this.horizontalLayout = horizontalLayout;
  }
  private collapseAfterChanged(collapseAfter: number) {
    this.collapseAfter = collapseAfter;
  }
  private async handleDepthSliderEvent() {
    await this.onCollapseAfterChanged();
    eventBus.$emit('handleDepthSliderEventEnd');
  }

}

function drawDepthLines(
  root: hcm.Component,
  group: d3.Selection<SVGGElement, any, HTMLElement, unknown>)
  : Promise<void> {
  const paths = Object.values<hcm.Component[]>(
    root.descendants().reduce(
      (table, node) => {
        const key = node.depth;
        if (!table[key]) {
          table[key] = [];
        }
        table[key].push(node);
        return table;
      }, // reducer
      {} // empty table
    )
  ).map(
    (nodes, index) => {
      nodes.sort((a, b) => a.x - b.x); // sorted in place
      const points = nodes.map<[number, number]>(node => [node.x, node.y]);
      return { id: `${index}`, d: (d3.line().curve(d3.curveLinear))(points) };
    } // mapper
  );
  group.selectAll('circle').remove();
  const depthLines = group
    .selectAll<SVGPathElement, {
      id: string;
      r: number;
    }>('path')
    .data(paths, d => d.id);

  const depthLinesEnter = depthLines
    .enter()
    .append('path')
    .attr('d', d => d.d)
    .attr('stroke-opacity', 0);
  const depthLinesUpdate = depthLines
    .merge(depthLinesEnter)
    .transition().duration(500)
    .attr('d', d => d.d)
    .attr('stroke-opacity', 0.4);
  depthLines
    .exit()
    .transition().duration(500)
    .attr('stroke-opacity', 0)
    .remove();
  return depthLinesUpdate.end();
}

function drawConcentricCircles(
  root: hcm.Component,
  group: d3.Selection<SVGGElement, any, HTMLElement, unknown>)
  : Promise<void> {
  const radii = Object.values<hcm.Component[]>(
    root.descendants().reduce(
      (table, node) => {
        const key = node.depth;
        if (!table[key]) {
          table[key] = [];
        }
        table[key].push(node);
        return table;
      }, // reducer
      {} // empty table
    )
  ).map(
    row => {
      const node = row.sort((a, b) => a.height - b.height).slice(-1)[0];
      return { id: `${node.depth}`, r: Math.sqrt(Math.pow(node.x, 2) + Math.pow(node.y, 2)) };
    } // mapper
  );
  group.selectAll('path').remove();
  const concentricCircles = group
    .selectAll<SVGCircleElement, {
      id: string;
      r: number;
    }>('circle')
    .data(radii, d => d.id);
  const concentricCirclesEnter = concentricCircles
    .enter()
    .append('circle')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', d => d.r)
    .attr('stroke-opacity', 0);
  const concentricCirclesUpdate = concentricCircles
    .merge(concentricCirclesEnter)
    .transition().duration(500)
    .attr('r', d => d.r)
    .attr('stroke-opacity', 0.4);
  concentricCircles
    .exit()
    .transition().duration(500)
    .attr('stroke-opacity', 0)
    .remove();
  return concentricCirclesUpdate.end();
}
